import React, {ReactNode} from 'react';
import I18n from 'app/i18n';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {Link} from 'react-router-dom';
import {getLinkPath} from 'app/utils/link_helper';

interface BalanceUserStatusProps {
    amountBalance: string
    amountActiveBets: string
    amountBonusBalance: string
    amountFrozenBalance: string
    frozenTooltip?: ReactNode;
    cashbackTooltip?: ReactNode;
    currentPercentCashback: number;
    onClose?: () => void;
}

type BlockKey = 'bonus' | 'frozen' | 'cashback';

interface BalanceUserStatusState {
    open: BlockKey | null;
}

export default class BalanceUserStatus extends React.Component<BalanceUserStatusProps, BalanceUserStatusState> {
    state: BalanceUserStatusState = {open: null};

    cashbackRef = React.createRef<HTMLButtonElement>();

    bonusRef = React.createRef<HTMLDivElement>();

    frozenRef = React.createRef<HTMLDivElement>();

    componentDidMount(): void {
        document.addEventListener('click', this.handleDocumentClick);
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.handleDocumentClick);
    }

    handleDocumentClick = (e: MouseEvent): void => {
        const t = e.target as Node;
        const clickedInside =
          this.bonusRef.current?.contains(t) ||
          this.frozenRef.current?.contains(t) ||
          this.cashbackRef.current?.contains(t);

        if (!clickedInside) {
            this.setState({open: null});
        }
    };

    toggle = (key: BlockKey) => (e: React.MouseEvent<HTMLElement>): void => {
        e.stopPropagation();
        this.setState(prev => ({open: prev.open === key ? null : key}));
    };

    tooltipClickHandler: React.MouseEventHandler<HTMLElement> = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };

    isZeroBalance = (val?: string): boolean => {
        if (!val) {
            return false;
        }

        const cleaned = val
            .replace(/\u00A0/gu, ' ')
            .replace(/\s+/gu, '')
            .replace(/[^\d,.-]/gu, '')
            .replace(/,/gu, '.');
        const n = Number(cleaned);
        return Number.isFinite(n) && n === 0;
    };

    onClose = (): void => {
        this.props.onClose?.();
    };

    render(): ReactNode {
        const {
            amountBalance,
            amountActiveBets,
            amountBonusBalance,
            amountFrozenBalance,
            frozenTooltip,
            cashbackTooltip,
            currentPercentCashback
        } = this.props;
        const {open} = this.state;

        return <div className="balance-user-status">
            <div className={`balance-amount${this.isZeroBalance(amountBalance) ? ' none' : ''}`}>
                <SpriteIcon
                    name="#svg-balance-money"
                    className="svg-default icon balance-icon"
                />
                <span className="amount">{amountBalance}</span>
                <span className={`additionally${this.isZeroBalance(amountActiveBets) ? ' none' : ''}`}>
                    ({amountActiveBets} {I18n.t('sum_of_active_bets')})
                </span>
                <div className="tooltip-cashback">
                    <button ref={this.cashbackRef} className="button-cashback" onClick={this.toggle('cashback')}>
                        <SpriteIcon name="#svg-balance-cashback" className="svg-default"/>
                        <span className="amount">{currentPercentCashback * 100}%</span>
                    </button>
                    <div className={`tooltip-amount${open === 'cashback' ? ' active' : ''}`} onClick={this.tooltipClickHandler}>
                        {cashbackTooltip}
                    </div>
                </div>
            </div>
            <div
                ref={this.bonusRef}
                className={`balance-amount tooltip${this.isZeroBalance(amountBonusBalance) ? ' none' : ''}`}
                onClick={this.toggle('bonus')}
            >
                <SpriteIcon
                    className="svg-default icon bonus-icon"
                    name="#svg-balance-bonus-money"
                />
                <span className="amount">{amountBonusBalance}</span>
                <div className={`tooltip-amount${open === 'bonus' ? ' active' : ''}`} onClick={this.tooltipClickHandler}>
                    <span>{I18n.t('user_bonus_balance')}</span>
                    <p>{I18n.t('user_bonus_balance_description')}</p>
                    <Link to={getLinkPath('/faq/bonus')} onClick={this.onClose}>
                        {I18n.t('read_more_u')}
                    </Link>
                </div>
            </div>
            <div
                ref={this.frozenRef}
                className={`balance-amount tooltip frozen-tooltip${this.isZeroBalance(amountFrozenBalance) ? ' none' : ''}`}
                onClick={this.toggle('frozen')}
            >
                <SpriteIcon
                    className="svg-default icon frozen-icon"
                    name="#svg-balance-frozen"
                />
                <span className="amount"> {amountFrozenBalance}</span>
                <div className={`tooltip-amount${open === 'frozen' ? ' active' : ''}`} onClick={this.tooltipClickHandler}>
                    {frozenTooltip}
                </div>
            </div>
        </div>;
    }
}
